import type { MouseEvent } from 'react';
import { useActiveAccount } from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { API } from '@/api';
import { useString } from '@/hooks/useString';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import TextField from '@/components/inputs/TextField';
import { classes, isDisabled, validateFields } from './utils';

type LoginDialogProps = {
  onClose: () => void;
};

export default function LoginDialog({ onClose }: LoginDialogProps) {
  const { onApiError, onLogin } = useAppState();
  const activeAccount = useActiveAccount();
  const [email, setEmail] = useString();
  const [password, setPassword] = useString();
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isLoggingIn, toggleLoggingIn] = useToggle();

  const handleLogin = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault();
      toggleLoggingIn(true);
      const invalid = validateFields(email, password);
      if (!isEmpty(invalid)) return setInvalidFields(invalid);
      const { accountType, accountId } = activeAccount!;
      await API.Users.login(email, password);
      await API.Users.setActiveAccount(accountType, accountId);
      toggleLoggingIn(false);
      onLogin();
    } catch (err) {
      toggleLoggingIn(false);
      onApiError(err, 'Error logging in');
    }
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        className={classes.title}
        title="Log in"
        subtitle="Your session is expired. Please re-authenticate to continue."
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <form className={classes.form}>
          <TextField
            error={!isValidField('email')}
            className={classes.field}
            name="email"
            label="Email"
            value={email}
            onChange={setEmail}
          />
          <TextField
            error={!isValidField('password')}
            className={classes.field}
            name="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
          />
        </form>
      </DialogBody>
      <DefaultDialogActions
        disabled={isDisabled(email, password)}
        pending={isLoggingIn}
        className={classes.actions}
        confirmLabel="Log in"
        pendingLabel="Logging in"
        onCancel={onClose}
        onConfirm={handleLogin}
      />
    </Dialog>
  );
}
